import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';

type AppUserType = 'candidate' | 'company' | 'admin';

export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return router.createUrlTree(['/Home']);
  }

  const token = getCleanStorageValue('token');
  const userType = getStoredUserType();
  const allowedUserTypes = normalizeAllowedTypes(route.data?.['allowedUserTypes']);
  const allowGuest = route.data?.['allowGuest'] === true;

  if (!token) {
    return allowGuest ? true : router.createUrlTree(['/Login'], {
      queryParams: { returnUrl: state.url }
    });
  }

  if (!allowedUserTypes.length) {
    return true;
  }

  if (userType && allowedUserTypes.includes(userType)) {
    return true;
  }

  return getUnauthorizedRedirect(router, userType);
};

function getUnauthorizedRedirect(router: Router, userType: AppUserType | null): UrlTree {
  if (userType === 'company') {
    return router.createUrlTree(['/companyprofile']);
  }

  if (userType === 'candidate') {
    return router.createUrlTree(['/profile']);
  }

  if (userType === 'admin') {
    return router.createUrlTree(['/Home']);
  }

  return router.createUrlTree(['/Login']);
}

function normalizeAllowedTypes(value: unknown): AppUserType[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => normalizeUserType(item))
    .filter((item): item is AppUserType => !!item);
}

function getStoredUserType(): AppUserType | null {
  const directType =
    getCleanStorageValue('user_type') ||
    getCleanStorageValue('account_type') ||
    getCleanStorageValue('role') ||
    getCleanStorageValue('user_role') ||
    getCleanStorageValue('type');

  const normalizedDirectType = normalizeUserType(directType);

  if (normalizedDirectType) {
    return normalizedDirectType;
  }

  const storedUser =
    getCleanStorageValue('user') ||
    getCleanStorageValue('currentUser') ||
    getCleanStorageValue('auth_user') ||
    getCleanStorageValue('userData') ||
    getCleanStorageValue('user_data');

  if (!storedUser) {
    return null;
  }

  try {
    const user = JSON.parse(storedUser);
    const possibleType =
      user?.user_type ||
      user?.account_type ||
      user?.role ||
      user?.user_role ||
      user?.type ||
      user?.data?.user_type ||
      user?.data?.account_type ||
      user?.data?.role ||
      user?.data?.user_role ||
      user?.data?.type ||
      user?.user?.user_type ||
      user?.user?.account_type ||
      user?.user?.role ||
      user?.user?.user_role ||
      user?.user?.type;

    return normalizeUserType(possibleType);
  } catch {
    return null;
  }
}

function normalizeUserType(value: unknown): AppUserType | null {
  const type = String(value || '').toLowerCase().trim();

  if (
    type === 'candidate' ||
    type === 'candidates' ||
    type === 'jobseeker' ||
    type === 'job_seeker' ||
    type === 'employee' ||
    type === 'employees' ||
    type === 'user' ||
    type === '9'
  ) {
    return 'candidate';
  }

  if (
    type === 'company' ||
    type === 'companies' ||
    type === 'employer' ||
    type === 'employers' ||
    type === 'business'
  ) {
    return 'company';
  }

  if (type === 'admin' || type === 'super_admin' || type === '1') {
    return 'admin';
  }

  return null;
}

function getCleanStorageValue(key: string): string | null {
  const value = window.localStorage.getItem(key);

  if (
    !value ||
    value === 'null' ||
    value === 'undefined' ||
    value === '[object Object]'
  ) {
    return null;
  }

  return value;
}
